/**
 * Environment Configuration
 *
 * Centralized, typed access to environment variables.
 * Validates required variables at startup.
 */

interface EnvConfig {
  // App Info
  appName: string;
  appVersion: string;

  // API Configuration
  apiBaseUrl: string;
  apiTimeout: number;

  // Organization (for local development)
  localOrgName: string;

  // Environment
  env: "development" | "production" | "staging";
  isDev: boolean;
  isProd: boolean;
  isStaging: boolean;

  // Feature Flags
  enableMockApi: boolean;

  // Datadog
  ddApplicationId: string;
  ddClientToken: string;
  ddEnv: string;

  // CARTO raster basemap (Leaflet PNG tiles). Empty → watermarked tiles.
  cartoBasemapKey: string;
}

function getEnvVar(key: string, defaultValue?: string): string {
  const value = import.meta.env[key as keyof ImportMetaEnv] as
    | string
    | undefined;
  if (value !== undefined) return value;
  if (defaultValue !== undefined) return defaultValue;
  throw new Error(`Missing required environment variable: ${key}`);
}

function createEnvConfig(): EnvConfig {
  const env = getEnvVar("VITE_ENV", "development") as EnvConfig["env"];

  return {
    // App Info
    appName: getEnvVar("VITE_APP_NAME", "StormImpact"),
    appVersion: getEnvVar("VITE_APP_VERSION", "1.0.0"),

    // API Configuration
    apiBaseUrl: getEnvVar("VITE_API_BASE_URL", "http://localhost:8000"),
    apiTimeout: parseInt(getEnvVar("VITE_API_TIMEOUT", "30000"), 10),

    // Organization for local development
    localOrgName: getEnvVar("VITE_LOCAL_ORG_NAME", "urbint-power-company-east"),

    // Environment
    env,
    isDev: env === "development",
    isProd: env === "production",
    isStaging: env === "staging",

    // Feature Flags
    enableMockApi: getEnvVar("VITE_ENABLE_MOCK_API", "false") === "true",

    // Datadog
    ddApplicationId: getEnvVar("VITE_DD_APPLICATION_ID", ""),
    ddClientToken: getEnvVar("VITE_DD_CLIENT_TOKEN", ""),
    ddEnv: getEnvVar("VITE_DD_ENV", ""),

    cartoBasemapKey: getEnvVar("VITE_CARTO_BASEMAP_KEY", ""),
  };
}

// Create and export the config singleton
const envConfig = createEnvConfig();

// Log config in development (excluding sensitive data)
if (envConfig.isDev) {
  console.log("[ENV Config]", {
    env: envConfig.env,
    apiBaseUrl: envConfig.apiBaseUrl,
    enableMockApi: envConfig.enableMockApi,
  });
}

export default envConfig;
